import type {
  Contraction,
  ContractionStats,
  ContractionTrend,
  ContractionPhase,
  EmergencyTrigger,
} from '../types';

/**
 * Calculate mean of an array of numbers
 */
function mean(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, val) => sum + val, 0) / values.length;
}

/**
 * Calculate standard deviation of an array of numbers
 */
function standardDeviation(values: number[]): number {
  if (values.length < 2) return 0;
  const avg = mean(values);
  if (avg === null) return 0;
  const squareDiffs = values.map((value) => Math.pow(value - avg, 2));
  return Math.sqrt(squareDiffs.reduce((sum, val) => sum + val, 0) / values.length);
}

/**
 * Calculate trend from intervals (are they getting shorter, longer, or stable?)
 */
function calculateTrend(intervals: number[]): ContractionTrend {
  if (intervals.length < 3) return 'stable';

  // Compare first half average to second half average
  const midpoint = Math.floor(intervals.length / 2);
  const firstHalf = intervals.slice(0, midpoint);
  const secondHalf = intervals.slice(midpoint);

  const firstAvg = mean(firstHalf);
  const secondAvg = mean(secondHalf);

  if (firstAvg === null || secondAvg === null) return 'stable';

  const difference = secondAvg - firstAvg;
  const threshold = 30; // 30 seconds threshold for change

  if (difference < -threshold) return 'decreasing'; // intervals getting shorter
  if (difference > threshold) return 'increasing'; // intervals getting longer
  return 'stable';
}

/**
 * Determine the current labor phase based on average interval
 */
export function getPhase(avgIntervalMinutes: number | null): ContractionPhase {
  if (avgIntervalMinutes === null || avgIntervalMinutes > 15) return 'early_latent';
  if (avgIntervalMinutes > 5) return 'late_latent';
  if (avgIntervalMinutes > 3) return 'active';
  return 'transition';
}

/**
 * Calculate statistics from contractions
 * Uses the last 6 completed contractions for calculations
 */
export function calculateContractionStats(
  contractions: Contraction[],
  maxContractions: number = 6
): ContractionStats {
  // Filter only completed contractions and sort by start time descending
  const completedContractions = contractions
    .filter((c) => c.end_time !== null && c.duration_seconds !== null)
    .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());

  // Take last N contractions for statistics
  const recentContractions = completedContractions.slice(0, maxContractions);

  // Calculate average duration
  const durations = recentContractions
    .map((c) => c.duration_seconds)
    .filter((d): d is number => d !== null);
  const avgDurationSeconds = mean(durations);

  // Calculate average interval
  const intervals = recentContractions
    .map((c) => c.interval_seconds)
    .filter((i): i is number => i !== null);
  const avgIntervalSeconds = mean(intervals);

  // Check regularity (standard deviation < 60 seconds)
  const stdDev = standardDeviation(intervals);
  const isRegular = intervals.length >= 3 && stdDev < 60;

  // Calculate trend
  const trend = calculateTrend(intervals);

  // Determine phase
  const avgIntervalMinutes = avgIntervalSeconds !== null ? avgIntervalSeconds / 60 : null;
  const phase = getPhase(avgIntervalMinutes);

  return {
    count: completedContractions.length,
    avgDurationSeconds,
    avgIntervalSeconds,
    trend,
    isRegular,
    phase,
  };
}

/**
 * Check for emergency triggers
 */
export function checkEmergencyTriggers(contractions: Contraction[]): EmergencyTrigger[] {
  const triggers: EmergencyTrigger[] = [];

  // Sort by start time descending
  const sortedContractions = [...contractions]
    .filter((c) => c.end_time !== null)
    .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());

  // Check for very long contraction (> 90 seconds)
  const latestContraction = sortedContractions[0];
  if (latestContraction && latestContraction.duration_seconds !== null) {
    if (latestContraction.duration_seconds > 90) {
      triggers.push({
        type: 'long_contraction',
        message: 'Ungewöhnlich lange Wehe – kontaktiere deine Hebamme',
        severity: 'warning',
      });
    }
  }

  // Check for very short intervals (< 2 minutes) over 3+ contractions
  const recentThree = sortedContractions.slice(0, 3);
  if (recentThree.length >= 3) {
    const shortIntervalCount = recentThree.filter(
      (c) => c.interval_seconds !== null && c.interval_seconds < 120 // < 2 minutes
    ).length;

    if (shortIntervalCount >= 3) {
      triggers.push({
        type: 'short_intervals',
        message: 'Wehen sehr schnell hintereinander – sofort ins Krankenhaus!',
        severity: 'critical',
      });
    }
  }

  return triggers;
}

/**
 * Get trend arrow symbol
 */
export function getTrendSymbol(trend: ContractionTrend): string {
  switch (trend) {
    case 'decreasing':
      return '↓';
    case 'increasing':
      return '↑';
    case 'stable':
    default:
      return '→';
  }
}

/**
 * Get trend color class
 */
export function getTrendColorClass(trend: ContractionTrend): string {
  switch (trend) {
    case 'decreasing':
      return 'text-orange-500'; // intervals getting shorter = more urgent
    case 'increasing':
      return 'text-green-500'; // intervals getting longer = less urgent
    case 'stable':
    default:
      return 'text-gray-500';
  }
}
